/**
 * Z101 SuperMaster API Client
 * Connects frontend to backend at api.z101.io
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.z101.io';

class ApiClient {
  constructor() {
    this.baseUrl = API_BASE_URL;
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('auth_token');
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();
    const { signal, ...restOptions } = options;

    const config = {
      ...restOptions,
      signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...restOptions.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      // Handle 401 - Token expired
      if (response.status === 401) {
        this.setToken(null);
        window.dispatchEvent(new CustomEvent('auth:logout'));
        const error = new Error('Session expired. Please login again.');
        error.status = 401;
        throw error;
      }

      // Handle rate limiting
      if (response.status === 429) {
        const retryAfter = parseInt(response.headers.get('Retry-After') || '60', 10);
        const error = new Error(`Rate limited. Try again in ${retryAfter} seconds.`);
        error.status = 429;
        error.retryAfter = retryAfter;
        throw error;
      }

      // Handle no content
      if (response.status === 204) {
        return null;
      }

      const data = await response.json();

      if (!response.ok) {
        const error = new Error(data.message || data.error || 'API request failed');
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return data;
    } catch (error) {
      // Don't log aborted requests
      if (error.name !== 'AbortError') {
        console.error(`API Error [${endpoint}]:`, error);
      }
      throw error;
    }
  }

  // GET request with signal support
  get(endpoint, params = {}, signal) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: 'GET', signal });
  }

  // POST request
  post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PUT request
  put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // PATCH request
  patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

// Create singleton instance
const api = new ApiClient();

export default api;

// ============================================
// AUTH ENDPOINTS
// ============================================

export const authApi = {
  login: (email, password) => api.post('/api/v1/auth/login', { email, password }),
  register: (data) => api.post('/api/v1/auth/register', data),
  logout: () => api.post('/api/v1/auth/logout'),
  refresh: () => api.post('/api/v1/auth/refresh'),
  me: () => api.get('/api/v1/users/me'),
};

// ============================================
// USERS ENDPOINTS
// ============================================

export const usersApi = {
  list: (params) => api.get('/api/v1/admin/users', params),
  get: (id) => api.get(`/api/v1/users/${id}`),
  update: (id, data) => api.put(`/api/v1/users/${id}`, data),
  delete: (id) => api.delete(`/api/v1/users/${id}`),
  sessions: (userId) => api.get(`/api/v1/users/${userId}/sessions`),
  revokeSession: (userId, sessionId) => api.delete(`/api/v1/users/${userId}/sessions/${sessionId}`),
};

// ============================================
// COMPANIES/TENANTS ENDPOINTS
// ============================================

export const tenantsApi = {
  list: (params) => api.get('/api/v1/companies', params),
  get: (id) => api.get(`/api/v1/companies/${id}`),
  create: (data) => api.post('/api/v1/companies', data),
  update: (id, data) => api.put(`/api/v1/companies/${id}`, data),
  delete: (id) => api.delete(`/api/v1/companies/${id}`),
  suspend: (id) => api.post(`/api/v1/admin/companies/${id}/suspend`),
  reactivate: (id) => api.post(`/api/v1/admin/companies/${id}/reactivate`),
  usage: (id) => api.get(`/api/v1/admin/companies/${id}/usage`),
};

// ============================================
// CAMPAIGNS ENDPOINTS
// ============================================

export const campaignsApi = {
  list: (params) => api.get('/api/v1/campaigns', params),
  get: (id) => api.get(`/api/v1/campaigns/${id}`),
  create: (data) => api.post('/api/v1/campaigns', data),
  update: (id, data) => api.put(`/api/v1/campaigns/${id}`, data),
  delete: (id) => api.delete(`/api/v1/campaigns/${id}`),
  start: (id) => api.post(`/api/v1/campaigns/${id}/start`),
  pause: (id) => api.post(`/api/v1/campaigns/${id}/pause`),
  stop: (id) => api.post(`/api/v1/campaigns/${id}/stop`),
  stats: (id) => api.get(`/api/v1/campaigns/${id}/stats`),
  clone: (id) => api.post(`/api/v1/campaigns/${id}/clone`),
};

// ============================================
// LEADS/CRM ENDPOINTS
// ============================================

export const leadsApi = {
  list: (params) => api.get('/api/v1/leads', params),
  get: (id) => api.get(`/api/v1/leads/${id}`),
  create: (data) => api.post('/api/v1/leads', data),
  update: (id, data) => api.put(`/api/v1/leads/${id}`, data),
  delete: (id) => api.delete(`/api/v1/leads/${id}`),
};

export const contactsApi = {
  list: (params) => api.get('/api/v1/contacts', params),
  get: (id) => api.get(`/api/v1/contacts/${id}`),
  create: (data) => api.post('/api/v1/contacts', data),
  update: (id, data) => api.put(`/api/v1/contacts/${id}`, data),
  delete: (id) => api.delete(`/api/v1/contacts/${id}`),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.request('/api/v1/contacts/import', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set content-type for FormData
    });
  },
};

export const dealsApi = {
  list: (params) => api.get('/api/v1/deals', params),
  get: (id) => api.get(`/api/v1/deals/${id}`),
  create: (data) => api.post('/api/v1/deals', data),
  update: (id, data) => api.put(`/api/v1/deals/${id}`, data),
  delete: (id) => api.delete(`/api/v1/deals/${id}`),
};

// ============================================
// EMAIL ENDPOINTS
// ============================================

export const emailApi = {
  accounts: {
    list: () => api.get('/api/v1/email-accounts'),
    create: (data) => api.post('/api/v1/email-accounts', data),
    delete: (id) => api.delete(`/api/v1/email-accounts/${id}`),
  },
  warmup: {
    status: () => api.get('/api/v1/warm-up/status'),
    start: (accountId) => api.post('/api/v1/warm-up/start', { accountId }),
    stop: (accountId) => api.post('/api/v1/warm-up/stop', { accountId }),
  },
  contacts: {
    list: (params) => api.get('/api/v1/email/contacts', params),
    import: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.request('/api/v1/email/contacts/import', {
        method: 'POST',
        body: formData,
        headers: {},
      });
    },
  },
};

// ============================================
// SMS ENDPOINTS
// ============================================

export const smsApi = {
  campaigns: {
    list: () => api.get('/api/v1/sms/campaigns'),
    create: (data) => api.post('/api/v1/sms/campaigns', data),
  },
  send: (data) => api.post('/api/v1/sms/send', data),
  analytics: () => api.get('/api/v1/sms/analytics'),
  contacts: {
    list: (params) => api.get('/api/v1/sms/contacts', params),
    import: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.request('/api/v1/sms/contacts/import', {
        method: 'POST',
        body: formData,
        headers: {},
      });
    },
  },
};

// ============================================
// ANALYTICS ENDPOINTS
// ============================================

export const analyticsApi = {
  dashboard: () => api.get('/api/v1/analytics/dashboard'),
  campaigns: (params) => api.get('/api/v1/analytics/campaigns', params),
  realtime: () => api.get('/api/v1/integration/analytics/realtime'),
  performance: (params) => api.get('/api/v1/integration/analytics/performance', params),
  growth: (params) => api.get('/api/v1/integration/analytics/growth', params),
  export: (params) => api.get('/api/v1/analytics/export', params),
};

// ============================================
// ADMIN ENDPOINTS
// ============================================

export const adminApi = {
  dashboard: () => api.get('/api/v1/admin/dashboard'),
  health: () => api.get('/api/v1/admin/health'),
  metrics: () => api.get('/api/v1/admin/metrics'),
  plans: {
    list: () => api.get('/api/v1/admin/plans'),
    create: (data) => api.post('/api/v1/admin/plans', data),
  },
  features: {
    list: () => api.get('/api/v1/admin/features'),
    enable: (companyId, feature) => api.post(`/api/v1/admin/companies/${companyId}/features/${feature}/enable`),
    disable: (companyId, feature) => api.post(`/api/v1/admin/companies/${companyId}/features/${feature}/disable`),
  },
  auditLogs: (params) => api.get('/api/v1/admin/audit-logs', params),
};

// ============================================
// BILLING ENDPOINTS
// ============================================

export const billingApi = {
  credits: () => api.get('/api/v1/billing/credits'),
  purchase: (data) => api.post('/api/v1/billing/purchase', data),
  usage: (params) => api.get('/api/v1/billing/usage', params),
  invoices: () => api.get('/api/v1/billing/invoices'),
};

// ============================================
// AI ENDPOINTS
// ============================================

export const aiApi = {
  generate: (data) => api.post('/api/v1/ai/generate', data),
  analyze: (data) => api.post('/api/v1/ai/analyze', data),
  models: () => api.get('/api/v1/ai/models'),
};

// ============================================
// VIDEO ENDPOINTS
// ============================================

export const videoApi = {
  list: (params) => api.get('/api/v1/videos', params),
  get: (id) => api.get(`/api/v1/videos/${id}`),
  process: (data) => api.post('/api/v1/videos/process', data),
  status: (id) => api.get(`/api/v1/videos/${id}/status`),
  delete: (id) => api.delete(`/api/v1/videos/${id}`),
};

// ============================================
// SOCIAL MEDIA ENDPOINTS
// ============================================

export const socialApi = {
  accounts: {
    list: () => api.get('/api/v1/social/accounts'),
    connect: (platform) => api.post('/api/v1/social/accounts/connect', { platform }),
    disconnect: (id) => api.delete(`/api/v1/social/accounts/${id}`),
  },
  posts: {
    list: (params) => api.get('/api/v1/social/posts', params),
    create: (data) => api.post('/api/v1/social/posts', data),
    schedule: (data) => api.post('/api/v1/social/posts/schedule', data),
  },
  analytics: () => api.get('/api/v1/social/analytics'),
};

// ============================================
// FRAUD/SECURITY ENDPOINTS
// ============================================

export const fraudApi = {
  events: (params) => api.get('/api/v1/fraud/events', params),
  score: (userId) => api.get(`/api/v1/fraud/score/${userId}`),
  block: (userId) => api.post(`/api/v1/fraud/block/${userId}`),
  unblock: (userId) => api.post(`/api/v1/fraud/unblock/${userId}`),
  rules: {
    list: () => api.get('/api/v1/fraud/rules'),
    update: (data) => api.put('/api/v1/fraud/rules', data),
  },
};

// ============================================
// ACTIVITY LOG ENDPOINTS
// ============================================

export const activityApi = {
  list: (params) => api.get('/api/v1/admin/audit-logs', params),
  export: (params) => api.get('/api/v1/admin/audit-logs/export', params),
};

// ============================================
// API KEYS ENDPOINTS
// ============================================

export const apiKeysApi = {
  list: () => api.get('/api/v1/api-keys'),
  create: (data) => api.post('/api/v1/api-keys', data),
  revoke: (id) => api.delete(`/api/v1/api-keys/${id}`),
};

// ============================================
// WEBHOOKS ENDPOINTS
// ============================================

export const webhooksApi = {
  list: () => api.get('/api/v1/webhooks'),
  create: (data) => api.post('/api/v1/webhooks', data),
  update: (id, data) => api.put(`/api/v1/webhooks/${id}`, data),
  delete: (id) => api.delete(`/api/v1/webhooks/${id}`),
  test: (id) => api.post(`/api/v1/webhooks/${id}/test`),
};

// ============================================
// L100 AI LOAD BALANCER ENDPOINTS
// ============================================

export const l100Api = {
  providers: {
    list: () => api.get('/api/v1/l100-providers'),
    get: (id) => api.get(`/api/v1/l100-providers/${id}`),
    update: (id, data) => api.put(`/api/v1/l100-providers/${id}`, data),
    disable: (id) => api.post(`/api/v1/l100-providers/${id}/disable`),
    enable: (id) => api.post(`/api/v1/l100-providers/${id}/enable`),
  },
  circuitBreaker: {
    status: () => api.get('/api/v1/l100-circuit-breaker'),
    reset: (providerId) => api.post(`/api/v1/l100-circuit-breaker/${providerId}/reset`),
  },
  dashboard: () => api.get('/api/v1/l100-dashboard'),
  config: {
    get: () => api.get('/api/v1/l100-config'),
    update: (data) => api.put('/api/v1/l100-config', data),
  },
};

// ============================================
// REVENUE/TRANSACTIONS ENDPOINTS
// ============================================

export const revenueApi = {
  dashboard: () => api.get('/api/v1/billing/dashboard'),
  transactions: (params) => api.get('/api/v1/billing/transactions', params),
  byPlan: () => api.get('/api/v1/billing/revenue-by-plan'),
  trend: (params) => api.get('/api/v1/billing/revenue-trend', params),
  export: (params) => api.get('/api/v1/billing/export', params),
};

// ============================================
// PLANS MANAGEMENT ENDPOINTS
// ============================================

export const plansApi = {
  list: () => api.get('/api/v1/admin/plans'),
  get: (id) => api.get(`/api/v1/admin/plans/${id}`),
  create: (data) => api.post('/api/v1/admin/plans', data),
  update: (id, data) => api.put(`/api/v1/admin/plans/${id}`, data),
  delete: (id) => api.delete(`/api/v1/admin/plans/${id}`),
  distribution: () => api.get('/api/v1/admin/plans/distribution'),
};

// ============================================
// MARKETING DASHBOARD ENDPOINTS
// ============================================

export const marketingApi = {
  // CEO Dashboard
  ceoDashboard: (companyId) => api.get('/api/v1/marketing/ceo-dashboard', { companyId }),
  heroMetrics: (params) => api.get('/api/v1/marketing/hero-metrics', params),
  aiFeed: (params) => api.get('/api/v1/marketing/ai-feed', params),
  budget: {
    overview: (companyId) => api.get('/api/v1/marketing/budget', { companyId }),
    update: (data) => api.put('/api/v1/marketing/budget', data),
  },
  approvalQueue: {
    list: (params) => api.get('/api/v1/marketing/approval-queue', params),
    approve: (id) => api.post(`/api/v1/marketing/approval-queue/${id}/approve`),
    reject: (id, reason) => api.post(`/api/v1/marketing/approval-queue/${id}/reject`, { reason }),
  },
  companies: {
    list: () => api.get('/api/v1/marketing/companies'),
    get: (id) => api.get(`/api/v1/marketing/companies/${id}`),
  },
};

// ============================================
// AI AGENTS ENDPOINTS
// ============================================

export const agentsApi = {
  list: (params) => api.get('/api/v1/agents', params),
  get: (id) => api.get(`/api/v1/agents/${id}`),
  create: (data) => api.post('/api/v1/agents', data),
  update: (id, data) => api.put(`/api/v1/agents/${id}`, data),
  delete: (id) => api.delete(`/api/v1/agents/${id}`),
  start: (id) => api.post(`/api/v1/agents/${id}/start`),
  pause: (id) => api.post(`/api/v1/agents/${id}/pause`),
  stop: (id) => api.post(`/api/v1/agents/${id}/stop`),
  stats: () => api.get('/api/v1/agents/stats'),
  logs: (id, params) => api.get(`/api/v1/agents/${id}/logs`, params),
};

// ============================================
// CONTENT ENDPOINTS
// ============================================

export const contentApi = {
  list: (params) => api.get('/api/v1/content', params),
  get: (id) => api.get(`/api/v1/content/${id}`),
  create: (data) => api.post('/api/v1/content', data),
  update: (id, data) => api.put(`/api/v1/content/${id}`, data),
  delete: (id) => api.delete(`/api/v1/content/${id}`),
  publish: (id) => api.post(`/api/v1/content/${id}/publish`),
  schedule: (id, data) => api.post(`/api/v1/content/${id}/schedule`, data),
  stats: () => api.get('/api/v1/content/stats'),
};

// ============================================
// ADS ENDPOINTS
// ============================================

export const adsApi = {
  campaigns: {
    list: (params) => api.get('/api/v1/ads/campaigns', params),
    get: (id) => api.get(`/api/v1/ads/campaigns/${id}`),
    create: (data) => api.post('/api/v1/ads/campaigns', data),
    update: (id, data) => api.put(`/api/v1/ads/campaigns/${id}`, data),
    delete: (id) => api.delete(`/api/v1/ads/campaigns/${id}`),
  },
  stats: (params) => api.get('/api/v1/ads/stats', params),
  spend: (params) => api.get('/api/v1/ads/spend', params),
  performance: (params) => api.get('/api/v1/ads/performance', params),
};

// ============================================
// SETTINGS ENDPOINTS
// ============================================

export const settingsApi = {
  get: () => api.get('/api/v1/admin/settings'),
  update: (data) => api.put('/api/v1/admin/settings', data),
  resetTenants: () => api.post('/api/v1/admin/settings/reset-tenants'),
  purgeSessions: () => api.post('/api/v1/admin/sessions/purge'),
};

// ============================================
// WHITELABEL ENDPOINTS
// ============================================

export const whitelabelApi = {
  get: () => api.get('/api/v1/admin/whitelabel'),
  update: (data) => api.put('/api/v1/admin/whitelabel', data),
  preview: (data) => api.post('/api/v1/admin/whitelabel/preview', data),
  publish: () => api.post('/api/v1/admin/whitelabel/publish'),
  reset: () => api.post('/api/v1/admin/whitelabel/reset'),
};
